/**
 * Entity Registry — single source of truth for "where does this entity type live?".
 *
 * Every entity type has exactly one canonical home, decided here. Consumers
 * never touch backing stores directly; they go through `entityRouter` hooks
 * which call into this registry to resolve the right store.
 *
 * Scopes:
 *   - 'space'   → spaceEntityStore  (per-space LRU=8 + IDB), keyed by activeSpaceId.
 *   - 'global'  → globalEntityStore (cross-space, eager, IDB-backed).
 *   - 'notes'   → notesEntityStore  (cross-space, lazily populated, higher
 *                 cardinality than global — see notesEntityStore.ts).
 *
 * App-owned entity types (Mail.*, Tasks.*, Bank.*, Automation.*, Database.*,
 * Contact) currently route to 'space'. Their dedicated app stores
 * (mailAppStore, tasksAppStore, etc.) own the source of truth for those types
 * and load their own data; the registry's space-store fallback is a no-op for
 * entities without a `spaceId`. If/when an app store wants to own routing,
 * add a new scope value here and register an adapter at module init time.
 *
 * Adding a new entity type? Register it in EXACT_SCOPES if it should live in
 * the global store; otherwise the default 'space' resolution is correct. The
 * unit test `entityRegistry.test.ts` is the canonical place to assert known
 * types map to the expected scope.
 *
 * The registry exposes both selectors and dispatchers so the live-update bus
 * (`orgYDocStore`) and system-command code paths can route changes uniformly.
 */

import type { BaseEntityType } from '../types/entities';

export type EntityScope = 'space' | 'global' | 'notes';

/** Exact-match table for global entity types. Everything else defaults to space. */
const EXACT_SCOPES: Readonly<Record<string, EntityScope>> = Object.freeze({
  Space: 'global',
  Org: 'global',
  MCPConnection: 'global',
  APIConnection: 'global',
  ManagedAPI: 'global',
  // Legacy "Integration" base type without dot — global as well.
  Integration: 'global',
  Note: 'notes',
  'Content.Note': 'notes',
});

/** Prefix rules applied when no exact match is found. Order matters. */
const PREFIX_SCOPES: ReadonlyArray<readonly [string, EntityScope]> = Object.freeze([
  ['Integration.', 'global'],
]);

/** Resolve an entity type to its canonical store scope. Unknown → 'space'. */
export function resolveScope(entityType: string | undefined | null): EntityScope {
  if (!entityType) return 'space';
  const exact = EXACT_SCOPES[entityType];
  if (exact) return exact;
  for (const [prefix, scope] of PREFIX_SCOPES) {
    if (entityType.startsWith(prefix)) return scope;
  }
  return 'space';
}

// ─────────────────────────────────────────────────────────────────────────────
// Registry adapters — each scope provides four operations.
// Implementations are wired up at module-init time by the corresponding store
// (spaceEntityStore, globalEntityStore, mailAppStore, etc.) so we avoid
// circular imports.
// ─────────────────────────────────────────────────────────────────────────────

export interface ScopeAdapter {
  /** Return the entity by id if the scope holds it, otherwise undefined. */
  getById: (id: string) => BaseEntityType | undefined;
  /** Return all entities of the given exact entityType this scope holds. */
  getByType: (entityType: string) => BaseEntityType[];
  /** Insert or update an entity in the scope's backing store. */
  upsert: (entity: BaseEntityType) => void;
  /** Remove an entity by id from the scope's backing store. */
  deleteEntity: (id: string) => void;
  /** Optional dump for debugging / cross-scope enumeration. */
  dumpAll?: () => BaseEntityType[];
}

const _adapters: Partial<Record<EntityScope, ScopeAdapter>> = {};

/**
 * Install an adapter for a given scope. Called once per scope at app boot
 * (typically at the bottom of the corresponding store module).
 *
 * Idempotent: re-registering a scope replaces the previous adapter, which is
 * the right behavior when stores reset on org switch / HMR.
 */
export function registerScopeAdapter(scope: EntityScope, adapter: ScopeAdapter): void {
  _adapters[scope] = adapter;
}

/** Internal — used by tests and the dump helper. */
export function _allAdapters(): Array<[EntityScope, ScopeAdapter]> {
  return Object.entries(_adapters) as Array<[EntityScope, ScopeAdapter]>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public registry surface — non-hook accessors for system commands and the
// live-update bus. React components should prefer the hooks in entityRouter.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Last-resort lookup consulted by `getById` when none of the scope adapters
 * own the id. Registered once by `entityByIdCache` at module-init time (same
 * self-registration pattern as `registerScopeAdapter`, chosen specifically to
 * avoid a direct import of a store module here).
 *
 * Why this exists: `spaceEntityStore`'s adapter only writes an upsert into a
 * space's cache entry if that space is one of the handful currently loaded
 * in this tab (see `_applyUpsertToCaches`) — an update for an entity whose
 * space isn't cached is otherwise silently dropped from the registry, even
 * though `entityMutations.upsertEntity` always writes it into
 * `useEntityByIdCache` unconditionally. Without this fallback, any consumer
 * of `entityRegistry.getById` (e.g. spaceStore's window-title sync) can miss
 * updates that `useEntity`/`useEntityById` (which read `entityByIdCache`
 * directly) see just fine.
 */
let _byIdFallback: ((id: string) => BaseEntityType | undefined) | null = null;

/** Register the last-resort by-id lookup. Idempotent — last call wins. */
export function registerByIdFallback(fn: (id: string) => BaseEntityType | undefined): void {
  _byIdFallback = fn;
}

/**
 * Look up an entity by id across all registered scopes.
 *
 * Order: space → global → notes → byId-cache fallback. The first scope that
 * owns the id wins; other scopes are not consulted.
 */
export function getById(id: string): BaseEntityType | undefined {
  if (!id) return undefined;
  for (const scope of ['space', 'global', 'notes'] as EntityScope[]) {
    const adapter = _adapters[scope];
    if (!adapter) continue;
    const found = adapter.getById(id);
    if (found) return found;
  }
  return _byIdFallback?.(id);
}

/**
 * Return all entities of the given exact entityType from the owning scope.
 */
export function getByType(entityType: string): BaseEntityType[] {
  const scope = resolveScope(entityType);
  const adapter = _adapters[scope];
  if (!adapter) return [];
  return adapter.getByType(entityType);
}

/**
 * Dispatch an upsert through the registry. The owning scope is determined by
 * the entity's `entityType` field.
 */
export function upsert(entity: BaseEntityType): void {
  if (!entity || !entity.entityType) return;
  const scope = resolveScope(entity.entityType);
  _adapters[scope]?.upsert(entity);
}

/**
 * Dispatch a delete through the registry. When `entityType` is known, dispatch
 * to the owning scope only; otherwise fan out to every registered scope (for
 * Y.Doc tombstones, where the deleted node's type is no longer readable).
 */
export function deleteEntity(id: string, entityType?: string): void {
  if (!id) return;
  if (entityType) {
    _adapters[resolveScope(entityType)]?.deleteEntity(id);
    return;
  }
  for (const adapter of Object.values(_adapters)) {
    adapter?.deleteEntity(id);
  }
}

/** Enumerate every entity across every scope (for debug / dev console). */
export function dumpAll(): Array<{ scope: EntityScope; entities: BaseEntityType[] }> {
  return _allAdapters().map(([scope, adapter]) => ({
    scope,
    entities: adapter.dumpAll?.() ?? [],
  }));
}

/**
 * Subscribe to "anything in the registry has changed" events. Used by app
 * stores (mail / tasks / bank / …) that listen for upstream entity mutations
 * to keep their derived caches in sync.
 *
 * Returns an unsubscribe function. The callback is invoked once per upstream
 * `bumpVersion` tick — internally we listen to both `useSpaceEntityStore` and
 * `useGlobalEntityStore`. Other adapter-owning stores (mail/tasks/…) call
 * `notifyChange()` directly when their own state mutates.
 */
const _changeListeners = new Set<() => void>();

export function subscribeToChanges(callback: () => void): () => void {
  _changeListeners.add(callback);
  return () => {
    _changeListeners.delete(callback);
  };
}

/**
 * Internal — called by adapters (or the registry's upsert/delete dispatchers)
 * whenever the registry's overall state changes. Most call sites won't need
 * this; the space and global stores wire it up at module init time.
 */
export function notifyChange(): void {
  for (const listener of _changeListeners) {
    try {
      listener();
    } catch (err) {
      // Listeners must not throw — if they do, isolate so other listeners run.
      console.error('[entityRegistry] subscribeToChanges listener threw:', err);
    }
  }
}

/** Convenience namespace export so callers can write `entityRegistry.getById(id)`. */
export const entityRegistry = {
  resolveScope,
  registerScopeAdapter,
  registerByIdFallback,
  getById,
  getByType,
  upsert,
  deleteEntity,
  dumpAll,
  subscribeToChanges,
  notifyChange,
};
